import React, { useState } from 'react';
import { ReportInfoPanel } from '../../shared/components/ReportInfoPanel';
import { useQuery } from '@apollo/client';
import ApolloError from '../../shared/components/ApolloError';
import GET_ORG_UNITS_PERFORMANCE_REPORT from '../queries/getOrgUnitPerformanceReportQuery';
import DeltaPerformanceGraph from '../../shared/components/DeltaPerformanceGraph';
import { APPRAISAL_SPONSOR } from '../../../constants/appraisal-team-member-types';
import UnitDetailsBox from '../../shared/components/UnitDetailsBox';
import _, { get } from 'lodash';
import { isUserFDATier } from '../../shared/services/checkUserTierService';
import PageInstructions from '../../shared/components/PageInstructions';
import { PERFORMANCE_METRIC_STATUSES } from '../../shared/constants/checkpoint-performance-report-metric-enums';
import DataPointInfoDetails from './DataPointInfoDetails';
import PerformanceItemInfoDetails from './PerformanceItemInfoDetails';
import SkeletonWrapper from '../../shared/components/skeleton/SkeletonWrapper';
import NotEnoughDataMessage from '../../shared/components/NotEnoughDataMessage';
import { toHumanReadableDate } from '../../shared/services/dateService';
import PerformanceItemMapper from '../mappers/performanceItemMapper';
import { INFO_BOX_TYPES } from '../constants/infoBoxTypes';

/**
 * @typedef {object} DeltaPerformanceReportProps
 * @property {number} orgUnitId
 * @property {string} tier
 */

/**
 * @param {DeltaPerformanceReportProps} props
 */
const DeltaPerformanceReport = props => {
  const [infoBoxType, setInfoBoxType] = useState(INFO_BOX_TYPES.DEFAULT);
  const [infoBoxData, setInfoBoxData] = useState(null);

  const { data, error, loading } = useQuery(GET_ORG_UNITS_PERFORMANCE_REPORT, {
    variables: { id: props.orgUnitId }
  });

  const handleClickOutside = () => {
    setInfoBoxType(INFO_BOX_TYPES.DEFAULT);
    setInfoBoxData(null);
  };

  const handleDateClick = dataPoint => {
    setInfoBoxType(INFO_BOX_TYPES.DATA_POINT);
    setInfoBoxData({ dataPoint });
  };

  const handleBarClick = dataPoint => {
    setInfoBoxType(INFO_BOX_TYPES.DATA_POINT);
    setInfoBoxData({ dataPoint });
  };

  const handleTitleClick = (event, performanceItem) => {
    event.stopPropagation();
    setInfoBoxType(INFO_BOX_TYPES.PERFORMANCE_ITEM);
    setInfoBoxData({ performanceItem });
  };

  const renderInfoBox = () => {
    switch (infoBoxType) {
      case INFO_BOX_TYPES.DATA_POINT:
        return <DataPointInfoDetails dataPoint={infoBoxData.dataPoint} />;
      case INFO_BOX_TYPES.PERFORMANCE_ITEM:
        return <PerformanceItemInfoDetails performanceItem={infoBoxData.performanceItem} />;
      case INFO_BOX_TYPES.DEFAULT:
      default:
        return (
          <span>Select a metric (graph title) or check point date (x-axis) for more info.</span>
        );
    }
  };

  const getGraphData = () => {
    const performanceItems = data.performanceOrgUnitReport.PerformanceItems;
    if (!performanceItems.length) return;

    return performanceItems.map(pi => {
      const dataPointsWithDates = _.chain(pi.CheckpointPerformanceItemDataPoints)
        .map(dp => ({ ...dp, Date: dp.Checkpoints.DataCollectionDate }))
        .sortBy(dp => dp.Date)
        .value();

      let prevActual = 0;
      if (dataPointsWithDates.length > 0) {
        // Shifting data points to get deltas
        const firstDataPoint = dataPointsWithDates.shift();
        prevActual = firstDataPoint.Actual;
      }
      let prevTransparent = 0;
      const basePerformanceItem = PerformanceItemMapper.performanceItemToGraphData(pi);

      return {
        ...basePerformanceItem,
        DataPoints: dataPointsWithDates.map(dp => {
          let delta = 0;
          let transparent = 0;

          if (dp.Status && dp.Status === PERFORMANCE_METRIC_STATUSES.Skip.Id) {
            transparent = prevTransparent;
          } else {
            delta = dp.Actual - prevActual;
            transparent = delta >= 0 ? prevTransparent : prevTransparent + delta;
            prevTransparent = prevTransparent + delta;
            prevActual = dp.Actual;
          }

          delta = Math.round((delta + Number.EPSILON) * 100) / 100;
          return {
            Id: dp.Id,
            Name: pi.MeasurementName,
            Date: dp.Date,
            DeltaIncrease: delta >= 0 ? delta : 0,
            DeltaDecrease: delta < 0 ? Math.abs(delta) : 0,
            Transparent: transparent,
            Actual: dp.Actual,
            Status: dp.Status,
            Notes: dp.Notes,
            ReporterName: dp.Checkpoints.ReporterName
          };
        })
      };
    });
  };

  if (error) {
    return <ApolloError />;
  }

  const buildReportGraphs = () => {
    if (loading) {
      return (
        <div className='actual-report__graph'>
          <SkeletonWrapper show={true} inheritParentSize inheritChildHeight />
        </div>
      );
    }

    const graphData = getGraphData();

    if (!graphData) {
      return (
        <div className='delta-report__graph'>
          <NotEnoughDataMessage />
        </div>
      );
    }

    return graphData
      .filter(pi => pi)
      .map(pi => {
        return (
          <DeltaPerformanceGraph
            key={pi.Id}
            performanceItem={pi}
            dateClick={handleDateClick}
            barClick={handleBarClick}
            titleClick={handleTitleClick}
          />
        );
      });
  };

  const renderUnitDetailsBox = () => {
    if (Object.keys(data).length > 0) {
      const orgUnit = get(data, 'performanceOrgUnitReport', {});
      const appraisalOrgUnits = get(orgUnit, 'AppraisalOrgUnits', []);
      const latestAppraisal = appraisalOrgUnits[0].Appraisal;
      const address = [
        orgUnit.Address1,
        orgUnit.Address2,
        orgUnit.City,
        orgUnit.State,
        orgUnit.ZipCode
      ]
        .filter(Boolean)
        .join(', ');

      const OHTs = orgUnit.MddapOfficeOfHealthTechnologies.map(x =>
        x.OfficeOfHealthTechnology.Value.split(':')[0].replace('OHT', '')
      ).join(', ');
      const enrollmentDate = toHumanReadableDate(orgUnit.EnrollmentDate);
      const appraisalTeamMembers = latestAppraisal.AppraisalTeamMembers;
      const sponsorName = appraisalTeamMembers.find(x => x.Type === APPRAISAL_SPONSOR).Individual
        .Name;

      return (
        <UnitDetailsBox
          data={{
            location: address,
            FEI: orgUnit.Fei,
            enrollmentDate: enrollmentDate,
            OHTs: OHTs,
            sponsorName: sponsorName
          }}
          showAppraisalDetails={false}
        />
      );
    }
  };

  const description =
    'Performance Report metrics represented graphically over all time, ' +
    'including only changes between check points (delta). ' +
    'Select a metric (graph title) or check point date (x-axis) for more info.';

  return (
    <div onClick={handleClickOutside}>
      <PageInstructions
        title='Delta Performance Report'
        description={description}
        fdaShareWarning={!isUserFDATier(props.tier)}
      />
      <div className='report__body'>
        <div className='report-info-panels'>
          <ReportInfoPanel
            panelName='report-info-panel__small'
            loading={loading}
            renderChildren={renderUnitDetailsBox}
          />
          <ReportInfoPanel panelName='report-info-panel__more-info report-info-panel__large'>
            {renderInfoBox()}
          </ReportInfoPanel>
        </div>
        <div className='delta-report__graphs'>{buildReportGraphs()}</div>
      </div>
    </div>
  );
};

export default DeltaPerformanceReport;
